import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API });

// Ingress returns ACAO:* which browsers reject with credentials, so we use a
// bearer token (stored in localStorage) instead of cookies for admin auth.
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("ec_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term", "campaign", "source", "gclid", "gbraid", "wbraid", "fbclid"];

// Capture UTM/source params once and persist them for the session.
export function captureUtm() {
  const params = new URLSearchParams(window.location.search);
  const stored = JSON.parse(sessionStorage.getItem("ec_utm") || "{}");
  let changed = false;
  UTM_KEYS.forEach((k) => {
    const v = params.get(k);
    if (v) { stored[k] = v; changed = true; }
  });
  if (!stored.referrer && document.referrer && !document.referrer.includes(window.location.host)) {
    stored.referrer = document.referrer;
    changed = true;
  }
  if (!stored.landing_page) { stored.landing_page = window.location.pathname; changed = true; }
  if (changed) sessionStorage.setItem("ec_utm", JSON.stringify(stored));
  return stored;
}

export function getUtm() {
  return JSON.parse(sessionStorage.getItem("ec_utm") || "{}");
}

const TOUCH_KEY = "ec_touch";
const TOUCH_TTL = 30 * 864e5;
const LATEST_TTL = 90 * 864e5;
const CLICK_IDS = ["gclid", "gbraid", "wbraid", "fbclid", "msclkid", "ttclid", "li_fat_id"];
const UTM5 = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];
const consented = () => localStorage.getItem("ec_cookie_consent") === "accepted";

// Detailed channel from one touch; "Unknown" when it can't be identified (never guessed).
export function classifyTouch(t = {}) {
  const src = (t.utm_source || "").toLowerCase();
  const med = (t.utm_medium || "").toLowerCase();
  const paid = /cpc|ppc|paid|ads?\b|display|sponsored/.test(med);
  const ref = (t.referrer || "").toLowerCase();
  const has = (re) => re.test(src) || re.test(ref);
  if (t.gclid || t.gbraid || t.wbraid || (/google|adwords/.test(src) && paid)) return "Google Ads";
  if (/instagram|insta|\big\b/.test(src) && paid) return "Instagram Ads";
  if (/facebook|fb|meta/.test(src) && paid) return "Facebook Ads";
  if (has(/instagram/)) return "Instagram Organic";
  if (has(/facebook|fb\.|^fb$/) || t.fbclid) return "Facebook Organic";
  if (has(/linkedin|lnkd/) || t.li_fat_id) return "LinkedIn";
  if (has(/reddit/)) return "Reddit";
  if (has(/tiktok/) || t.ttclid) return "TikTok";
  if (has(/twitter|t\.co\/|x\.com|whatsapp|pinterest|youtube|threads/)) return "Other Social";
  if (/referral|agent|partner|pattinson/.test(src)) return "Professional Referral";
  if (!src && /\/\/(www\.)?google\./.test(ref)) return "Organic Google Search";
  if (!src && !ref && !CLICK_IDS.some((k) => t[k])) return t.first_visit ? "Direct" : "Unknown";
  return "Unknown";
}

// Persisted (localStorage) only after "Accept all": first touch 30 days, latest touch 90 days.
const expired = (t, ttl) => !t || Date.now() - (t.saved_at || Date.parse(t.at) || 0) > ttl;

function readTouches() {
  let v = null;
  try { v = JSON.parse(localStorage.getItem(TOUCH_KEY) || sessionStorage.getItem(TOUCH_KEY) || "null"); } catch { v = null; }
  if (!v) return null;
  if (v.latest && expired(v.latest, LATEST_TTL)) v.latest = null;
  if (v.first && expired(v.first, TOUCH_TTL)) v.first = null;
  if (!v.first && !v.latest) { localStorage.removeItem(TOUCH_KEY); return null; }
  return v;
}

export function persistTouches(v = readTouches()) {
  if (!v) return;
  if (consented()) { localStorage.setItem(TOUCH_KEY, JSON.stringify(v)); sessionStorage.removeItem(TOUCH_KEY); }
  else { sessionStorage.setItem(TOUCH_KEY, JSON.stringify(v)); localStorage.removeItem(TOUCH_KEY); }
}

// First-touch is kept; latest-touch updates whenever a new identifiable signal arrives.
export function captureTouch() {
  const p = new URLSearchParams(window.location.search);
  const ext = document.referrer && !document.referrer.includes(window.location.host) ? document.referrer : "";
  const t = { at: new Date().toISOString(), saved_at: Date.now(), landing_page: window.location.pathname, referrer: ext };
  [...UTM5, ...CLICK_IDS].forEach((k) => { const v = p.get(k); if (v) t[k] = v; });
  const signal = ext || UTM5.some((k) => t[k]) || CLICK_IDS.some((k) => t[k]);
  const cur = readTouches();
  const next = cur || { first: null, latest: null };
  if (!next.first) next.first = { ...t, first_visit: !cur };
  else if (signal) next.latest = t;
  next.first.source = classifyTouch(next.first);
  if (next.latest) next.latest.source = classifyTouch(next.latest);
  persistTouches(next);
  return next;
}

export const getTouches = () => readTouches() || {};

// derive a friendly source from utm/referrer
export function deriveSource() {
  const u = getUtm();
  if (u.source) return u.source;
  return getTouches().first?.source || classifyTouch({ ...u, first_visit: !u.referrer });
}

export async function track(event, meta = {}) {
  try {
    await api.post("/analytics/track", { event, path: window.location.pathname, meta: { ...getUtm(), ...meta } });
  } catch (e) { /* analytics never blocks UX */ }
}

export async function submitLead(payload) {
  const utm = getUtm();
  const body = {
    ...payload,
    source: payload.source || deriveSource(),
    campaign: utm.utm_campaign || utm.campaign || "",
    utm_source: utm.utm_source || "",
    utm_medium: utm.utm_medium || "",
    utm_campaign: utm.utm_campaign || "",
  };
  const { data } = await api.post("/leads", body);
  return data;
}

export async function submitSellEnquiry(payload) {
  const p = new URLSearchParams(window.location.search);
  const utm = { ...getUtm() };
  ["utm_content", "utm_term"].forEach((k) => { const v = p.get(k); if (v) utm[k] = v; });
  const tc = getTouches();
  const body = { ...payload, source: payload.source || deriveSource(), source_url: window.location.href, utm,
    touch: { first: tc.first || null, latest: tc.latest || null, consent: consented() } };
  const { data } = await api.post("/sell-enquiry", body);
  return data;
}

export async function submitInvestorApplication(payload) {
  const p = new URLSearchParams(window.location.search);
  const utm = { ...getUtm() };
  ["utm_content", "utm_term"].forEach((k) => { const v = p.get(k); if (v) utm[k] = v; });
  const body = { ...payload, source: payload.source || deriveSource(), source_url: window.location.href, utm };
  const { data } = await api.post("/investor-application", body);
  return data;
}

export async function uploadImage(file) {
  const form = new FormData();
  form.append("file", file);
  const { data } = await api.post("/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
  return data;
}

export async function uploadAdminImage(file) {
  const form = new FormData();
  form.append("file", file);
  const { data } = await api.post("/admin/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
  return `${API}/public/images/${data.id}`;
}
